import { DataGridPro, GridColDef, gridClasses } from '@mui/x-data-grid-pro';
import HeaderBar from './HederBar';
import { useSearchParams } from 'react-router-dom';
import { forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react';
import { ReportType } from '@declarations/common/reportType';
import { Box } from '@mui/material';
import userFormatter from '@components/DataGrid/renders/userFormatter';
import dateFormatter from '@components/DataGrid/renders/dateFormatter';
import attachmentsRender from '@components/DataGrid/renders/attachmentsRender';
import ClinicalLogRecordService from '@api/clinicalLogRecordService';
import LogRecordContainer from './LogRecordContainer';
import yesNoFormatter from '@components/DataGrid/renders/yesNoFormatter';

type Props = {
  reportType: ReportType;
};

const columns: GridColDef<any>[] = [
  {
    field: 'author',
    headerName: 'Author',
    flex: 0.3,
    valueFormatter: userFormatter,
  },
  {
    field: 'createdAt',
    headerName: 'Date',
    flex: 0.3,
    valueFormatter: dateFormatter,
  },
  {
    field: 'note',
    headerName: 'Notes',
    flex: 1,
  },
  {
    field: 'legalReviewRecommended',
    headerName: 'Legal Rev. Recommended',
    flex: 0.4,
    valueFormatter: yesNoFormatter,
  },
  {
    field: 'attachments',
    headerName: 'Attachments',
    flex: 0.3,
    sortable: false,
    renderCell: attachmentsRender,
  },
];

const ClinicalLogRecord = forwardRef<{ refresh: () => void }, Props>(({ reportType }: Props, ref) => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const id = Number(searchParams.get('id'));

  const fetchRecord = async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    const response = await ClinicalLogRecordService.get({ reportId: Number(id), reportType });
    setData(response.data);
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    refresh: () => { fetchRecord(); }
  }), []);

  useEffect(() => {
    fetchRecord();
  }, [searchParams]);

  return (
    <>
      <Box sx={{ mt: '32px' }}>
        <HeaderBar title="Log Record" />
        <LogRecordContainer rows={data.length}>
          <DataGridPro
            rows={data}
            autoHeight={data.length <= 5}
            rowSelection={false}
            disableColumnMenu
            disableColumnFilter
            columns={columns}
            loading={loading}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
                whiteSpace: 'pre-line',
              },
            }}
            getRowHeight={() => 'auto'}
          />
        </LogRecordContainer>
      </Box>
    </>
  );
});

export default memo(ClinicalLogRecord);
